import React, {
  useEffect,
  useState,
  useReducer,
  useCallback,
  useRef
} from 'react';

import { connect } from 'react-redux';
import { push } from 'connected-react-router';

import jwt from 'jsonwebtoken';
import Cookies from 'universal-cookie';

import NavBarView from '../view/NavBarView';
import NavBarMobileView from '../view/NavBarMobileView';
import getTenant from '../../utils/getTenant';
import SessionTimeouts from '../../utils/SessionTimeouts';
import getLogoSrc from '../../utils/getLogoSrc';
import { useHistory } from 'react-router';
import useGlobalVariables from '../../hooks/useGlobalVariables';
import capitalizeFirst from '../../utils/capitalizeFirst';
import useTranslations from '../../hooks/useTranslations';
import useMobileNavAPI from '../../hooks/useMobileNavAPI';
import getJWT from '../../utils/getJWT';
import { isIncludedInLicense } from '../../utils/isIncludedInLicense';
import FormsService from '../../services/FormsService';
import { APP_LICENSE } from '../../GetGlobalProps';
import { VINCI_SUPPORT_EMAIL } from '../../utils/Constants';
import getEnv from '../../utils/getEnv';
const cookies = new Cookies();

const env = getEnv();

const NavBarBtns = [
  {
    display: 'nav.buttons.forms',
    id: 'forms',
    link: '/forms',
    privileges: 'form:write'
  },
  {
    display: 'nav.buttons.users',
    id: 'users',
    link: '/users',
    privileges: 'user:write'
  },
  {
    display: 'nav.buttons.reports',
    id: 'reports',
    link: '/reports',
    privileges: 'user:read'
  },
  ...(env === 'development'
    ? [
        {
          display: 'nav.buttons.new_reports',
          id: 'new_reports',
          link: '/new_reports',
          privileges: 'user:read'
        }
      ]
    : []),
  {
    display: 'nav.buttons.audit_trail',
    id: 'audit',
    link: '/audit',
    privileges: 'audit_form:read'
  },
  {
    display: 'nav.buttons.settings',
    id: 'settings',
    link: '/settings',
    privileges: 'api:write'
  }
];

const NavigationBarReducer = (state, action) => {
  const { type, payload, index } = action;

  switch (type) {
    case 'update':
      return { ...state, ...payload };

    default:
      return state;
  }
};

const NavBarEngine = ({
  dispatch: reduxDispatch,
  nav,
  page,
  turnedOffSSO,
  noshadow
}) => {
  const [data, dispatch] = useReducer(NavigationBarReducer, {
    firstName: '',
    lastName: '',
    privileges: [],
    dynamicNavBtns: []
  });
  const { deleteJWT, isMobileView, licenseType } = useGlobalVariables();
  const mobileNavAPI = useMobileNavAPI();
  const t = useTranslations();
  const [isTimeoutDialogOpen, setIsTimeoutDialogOpen] = useState(false);
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState(null);
  const [isMenuReady, setIsMenuReady] = useState(false);
  const AMLFormId = useRef(''); // AML (OT light) will have 1 form, We need it's ID to reference it from the menu

  const history = useHistory();

  const getBtnDisplayName = useCallback(
    pageId => {
      switch (pageId) {
        case 'welcome':
          return t('nav.buttons.home');
        case 'dashboard':
          return t('nav.buttons.assignments');
        case 'new_reports':
          return t('nav.buttons.new_reports');
        default:
          const res = NavBarBtns.concat(data.dynamicNavBtns).find(
            b => b.id === pageId
          );
          return res ? t(res.display) : capitalizeFirst(pageId);
      }
    },
    [data]
  );

  useEffect(() => {
    const init = () => {
      let decoded = '',
        sessionExpiryTimestamp;
      const authHeader = cookies.get(`auth-header-${getTenant()}`) || '';
      const jwtToken = authHeader.replace(/(Bearer: )/, '');
      if (jwtToken) {
        decoded = jwt.decode(jwtToken);
        if (decoded) {
          sessionExpiryTimestamp = decoded.exp * 1000;
          if (sessionExpiryTimestamp && sessionExpiryTimestamp > 0) {
            if (window.onTenMinutesIdle) {
              clearInterval(window.onTenMinutesIdle);
            }
            window.onTenMinutesIdle = setInterval(onTenMinutesIdle, 600000);
          } else {
            onLogout();
            return;
          }
          if (
            decoded.userProfile &&
            (decoded.userProfile.firstName || decoded.userProfile.lastName)
          ) {
            dispatch({
              type: 'update',
              payload: {
                firstName: decoded.userProfile.firstName || 'Anonymous',
                lastName: decoded.userProfile.lastName || 'User'
              }
            });
            if (decoded.userId) {
              decoded.userProfile.userId = decoded.userId;
            }
            reduxDispatch({
              type: 'userProfile/SET_USERPROFILE',
              payload: decoded.userProfile
            });
          }
          if (decoded.privileges && decoded.privileges.length) {
            const privileges = decoded.privileges;
            let dynamicNavBtns = [];
            if (privileges.indexOf('form:write') === -1) {
              dynamicNavBtns.push({
                display: 'nav.buttons.home',
                id: 'welcome',
                alias: 'dashboard',
                link: '/welcome',
                privileges: 'form:read'
              });
            }
            dispatch({
              type: 'update',
              payload: {
                privileges,
                dynamicNavBtns
              }
            });
          }
        }
      }
    };
    init();
    return () => {
      mobileNavAPI.setTitle('');
      mobileNavAPI.setUseBackBtn(false);
    };
  }, []);

  useEffect(() => {
    if (getJWT()) {
      FormsService.getCompactForms({ limit: 1 }).then(response => {
        const formId = (response.results[0] || {}).id;
        AMLFormId.current = formId;
        dispatch({
          type: 'update',
          payload: {
            dynamicNavBtns: [
              {
                display: 'nav.buttons.aml-responses',
                id: 'aml-responses',
                alias: 'responses',
                link: `/responses/${formId}`,
                privileges: 'form:write'
              },
              ...data.dynamicNavBtns
            ]
          }
        });
        setIsMenuReady(true);
      });
    } else {
      setIsMenuReady(true);
    }
  }, []);

  const onLogout = () => {
    deleteJWT(getTenant());
    cookies.set('is-saml', '', { path: '/' });
    // store.dispatch(push('/login'));
    reduxDispatch(push('/login'));
    dispatch({
      type: 'update',
      payload: {
        firstName: '',
        lastName: ''
      }
    });
    setUserMenuAnchorEl(null);
  };

  const onTenMinutesIdle = () => {
    SessionTimeouts.tenMinuteSessionCheckup.call(SessionTimeouts, {
      openDialog: () => setIsTimeoutDialogOpen(true),
      logout: onLogout
    });
  };

  const goTo = path => {
    history.push(path);
  };

  const onLogoClick = useCallback(() => {
    const isVinciSupport =
      ((getJWT() || {}).userProfile || {}).email === VINCI_SUPPORT_EMAIL;

    if (data.privileges.indexOf('form:write') !== -1) {
      // admin privileges
      if (licenseType === APP_LICENSE.AML && !isVinciSupport) {
        goTo(`/responses/${AMLFormId.current}`);
      } else {
        goTo('/forms');
      }
    } else {
      goTo('/welcome');
    }
  }, [data, licenseType]);

  const viewProps = {
    data,
    logoSrc: getLogoSrc(),
    goTo,
    pageId: (page && page.id) || '',
    userMenuAnchorEl,
    setUserMenuAnchorEl,
    onLogout,
    isSaml: !!cookies.get('is-saml') || false,
    turnedOffSSO,
    showNavBarBtns: !!getJWT(),
    isTimeoutDialogOpen,
    setIsTimeoutDialogOpen,
    navbarBtns: data.dynamicNavBtns
      .concat(NavBarBtns)
      .filter(btn => isIncludedInLicense(`nav-btn-${btn.id}`)),
    getBtnDisplayName,
    onLogoClick,
    isMenuReady,
    noshadow
  };

  return isMobileView ? (
    <NavBarMobileView {...viewProps} />
  ) : (
    <NavBarView {...viewProps} />
  );
};

const mapStateToProps = (store, props) => {
  return {
    turnedOffSSO: store.state.turnedOffSSO
  };
};

export default connect(mapStateToProps)(NavBarEngine);
